import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Edit,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
  ArrowLeftRight,
  X,
  Loader2,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { showSuccess, showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { refundsService } from '@/services/refundsService'
import { paymentReturnService } from '@/services/paymentReturnService'
import { paymentsService } from '@/services/paymentsService'
import { RefundListItem, RefundVendor, PaymentReturnBill, PaymentReturnInsertData, PaymentReturnVm, PaymentAccount } from '@/api/types'

export const RefundsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [refunds, setRefunds] = useState<RefundListItem[]>([])
  const [vendors, setVendors] = useState<RefundVendor[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1) // Reset to first page when page size changes
  }
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedVendorId, setSelectedVendorId] = useState<number | undefined>(undefined)
  const [refundDateFrom, setRefundDateFrom] = useState<string>('')
  const [refundDateTo, setRefundDateTo] = useState<string>('')
  const [dueDateFrom, setDueDateFrom] = useState<string>('')
  const [dueDateTo, setDueDateTo] = useState<string>('')

  // Vendor Credit Modal State
  const [selectedRefund, setSelectedRefund] = useState<RefundListItem | null>(null)
  const [showVendorCreditModal, setShowVendorCreditModal] = useState(false)
  const [vendorCreditLoading, setVendorCreditLoading] = useState(false)
  const [vendorCreditSaving, setVendorCreditSaving] = useState(false)
  const [vendorBills, setVendorBills] = useState<PaymentReturnBill[]>([])
  const [insertData, setInsertData] = useState<PaymentReturnInsertData | null>(null)
  const [accounts, setAccounts] = useState<PaymentAccount[]>([])
  const [vendorCreditForm, setVendorCreditForm] = useState<{
    billId: number
    billNo: string
    recipientAccount: number
    destinationAccount: number
    amount: number
    date: string
    note: string
    returnType: string
  }>({
    billId: 0,
    billNo: '',
    recipientAccount: 0,
    destinationAccount: 0,
    amount: 0,
    date: new Date().toISOString().split('T')[0],
    note: '',
    returnType: 'Cash',
  })

  const branchId = user?.selectedBranch?.branchId

  // Load vendors for filter
  useEffect(() => {
    if (!branchId) return
    const loadVendors = async () => {
      try {
        const vendorList = await refundsService.getAllVendors(branchId)
        setVendors(vendorList)
      } catch (error: any) {
        // Error handled silently
      }
    }
    loadVendors()
  }, [branchId])

  // Load accounts for vendor credit
  useEffect(() => {
    if (!branchId) return
    const loadAccounts = async () => {
      try {
        const accountsData = await paymentsService.getAllAccounts(branchId)
        setAccounts(accountsData || [])
      } catch (error: any) {
        console.error('Failed to load accounts', error)
      }
    }
    loadAccounts()
  }, [branchId])

  // Load refunds
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadRefunds = async () => {
      try {
        setLoading(true)
        const response = await refundsService.getRefunds(branchId, pageNumber, pageSize)
        const list = response.data || []
        setRefunds(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setRefunds([])
        setTotalCount(0)
        setTotalPages(1)
        if (!isNoData) {
          toast.error(error.response?.data?.message || t('refunds.failedToLoadRefunds'))
        }
      } finally {
        setLoading(false)
      }
    }

    loadRefunds()
  }, [branchId, pageNumber, pageSize])

  const filteredRefunds = useMemo(() => {
    return refunds.filter((refund) => {
      if (selectedVendorId && refund.vendorId !== selectedVendorId) {
        return false
      }

      if (refundDateFrom && refund.refundDate && refund.refundDate < refundDateFrom) {
        return false
      }

      if (refundDateTo && refund.refundDate && refund.refundDate > refundDateTo) {
        return false
      }

      if (dueDateFrom && refund.dueDate && refund.dueDate < dueDateFrom) {
        return false
      }

      if (dueDateTo && refund.dueDate && refund.dueDate > dueDateTo) {
        return false
      }

      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase()
        return (
          (refund.refundNo || refund.refundNumber || '').toLowerCase().includes(query) ||
          (refund.billNo || refund.billNumber || '').toLowerCase().includes(query) ||
          (refund.vendorName || '').toLowerCase().includes(query)
        )
      }

      return true
    })
  }, [refunds, searchQuery, selectedVendorId, refundDateFrom, refundDateTo, dueDateFrom, dueDateTo])

  const handleAddNew = () => {
    navigate('/purchases/refunds/new')
  }

  const handleEdit = (refund: RefundListItem) => {
    const refundId = refund.refundId
    if (!refundId) {
      toast.error('Invalid refund ID')
      return
    }
    navigate(`/purchases/refunds/${refundId}/edit`)
  }

  const handleCopy = () => {
    const tableData = filteredRefunds.map(r => ({
      'Cr Note No': r.refundNo || r.refundNumber || '-',
      'Bill No': r.billNo || r.billNumber || '-',
      Vendor: r.vendorName || '-',
      Date: r.refundDate || '-',
      'Due Date': r.dueDate || '-',
      Amount: r.amount || r.total || 0,
    }))
    const csv = [
      Object.keys(tableData[0] || {}).join(','),
      ...tableData.map(row => Object.values(row).join(','))
    ].join('\n')
    navigator.clipboard.writeText(csv)
    toast.success(t('refunds.copiedToClipboard'))
  }

  const handleExportCSV = () => {
    const tableData = filteredRefunds.map(r => ({
      'Cr Note No': r.refundNo || r.refundNumber || '-',
      'Bill No': r.billNo || r.billNumber || '-',
      Vendor: r.vendorName || '-',
      Date: r.refundDate || '-',
      'Due Date': r.dueDate || '-',
      Amount: r.amount || r.total || 0,
    }))
    const csv = [
      Object.keys(tableData[0] || {}).join(','),
      ...tableData.map(row => Object.values(row).join(','))
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `refunds_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('refunds.exportedToCsv'))
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // Get user IP address
  const getUserIPAddress = async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip || '0.0.0.0'
    } catch (error) {
      console.warn('Failed to get IP address from service, using fallback')
      return '0.0.0.0'
    }
  }

  // Handle Vendor Credit button click
  const handleVendorCreditClick = async (refund: RefundListItem) => {
    const vendorId = refund.vendorId
    // Try multiple possible field names for bill ID
    const billId = refund.billId || (refund as any).refBillId || 0

    if (!vendorId) {
      toast.error('Vendor ID not found')
      return
    }

    setSelectedRefund(refund)
    setShowVendorCreditModal(true)
    setVendorCreditLoading(true)

    try {
      // Step 1: Get bills by vendor ID
      const bills = await paymentReturnService.getBillsByVendorId(vendorId)
      setVendorBills(bills)

      if (bills.length === 0) {
        setVendorCreditLoading(false)
        setShowVendorCreditModal(false)
        resetVendorCreditForm()
        await showError('Error', 'No bills found for this vendor')
        return
      }

      // Step 2: Find matching bill ID
      let matchedBillId = billId
      if (billId > 0) {
        const matchedBill = bills.find(b => b.billId === billId)
        if (!matchedBill) {
          // If billId from refund doesn't match, use first bill
          matchedBillId = bills[0].billId
        }
      } else {
        // If no billId in refund, use first bill
        matchedBillId = bills[0].billId
      }

      if (matchedBillId > 0 && branchId) {
        // Step 3: Get insert data
        const insertDataResponse = await paymentReturnService.getInsertData(branchId, matchedBillId)
        setInsertData(insertDataResponse)

        // Step 4: Populate form
        const matchedBill = bills.find(b => b.billId === matchedBillId)
        setVendorCreditForm({
          billId: matchedBillId,
          billNo: matchedBill?.billNo || '',
          recipientAccount: insertDataResponse.paymentReturn.recipientAccount,
          destinationAccount: insertDataResponse.paymentReturn.destinationAccount,
          amount: insertDataResponse.paymentReturn.amount,
          date: new Date().toISOString().split('T')[0],
          note: '',
          returnType: 'Cash',
        })
      } else {
        toast.error('Unable to determine bill ID')
      }
    } catch (error: any) {
      console.error('Failed to load vendor credit data', error)
      await showError('Error', error.response?.data?.message || 'Failed to load vendor credit data')
    } finally {
      setVendorCreditLoading(false)
    }
  }

  // Handle Vendor Credit Save
  const handleVendorCreditSave = async () => {
    if (!branchId || !selectedRefund) return

    if (!vendorCreditForm.destinationAccount || vendorCreditForm.destinationAccount === 0) {
      toast.error('Please select destination account')
      return
    }

    if (!vendorCreditForm.amount || vendorCreditForm.amount <= 0) {
      toast.error('Please enter a valid amount')
      return
    }

    if (!vendorCreditForm.date) {
      toast.error('Please select a date')
      return
    }

    if (!vendorCreditForm.returnType) {
      toast.error('Please select return type')
      return
    }

    setVendorCreditSaving(true)

    try {
      // Get user IP address
      const ipAddress = await getUserIPAddress()

      const saveData: PaymentReturnVm = {
        id: 0,
        billId: vendorCreditForm.billId,
        vendorId: selectedRefund.vendorId || 0,
        recipientAccount: vendorCreditForm.recipientAccount,
        destinationAccount: vendorCreditForm.destinationAccount,
        amount: vendorCreditForm.amount,
        date: new Date(vendorCreditForm.date).toISOString(),
        note: vendorCreditForm.note,
        returnType: vendorCreditForm.returnType,
        branchId: branchId,
        ipAddress: ipAddress,
      }

      // Log the exact structure being sent
      console.log('=== Vendor Credit Save Request ===')
      console.log('Save Data Structure:', JSON.stringify(saveData, null, 2))
      console.log('Save Data Object:', saveData)
      console.log('===================================')

      await paymentReturnService.savePaymentReturn(saveData)
      await showSuccess('Success', 'Vendor credit saved successfully')
      setShowVendorCreditModal(false)
      resetVendorCreditForm()
    } catch (error: any) {
      console.error('❌ Failed to save vendor credit', error)
      console.error('❌ Error Response:', error.response)
      console.error('❌ Error Response Data:', error.response?.data)
      console.error('❌ Error Message:', error.message)
      console.error('❌ Error Stack:', error.stack)
      
      const errorMessage = 
        error.response?.data?.message || 
        error.response?.data?.error || 
        error.response?.data?.title ||
        error.message || 
        'Failed to save vendor credit'
      
      await showError('Error', errorMessage)
    } finally {
      setVendorCreditSaving(false)
    }
  }

  // Reset vendor credit form
  const resetVendorCreditForm = () => {
    setVendorCreditForm({
      billId: 0,
      billNo: '',
      recipientAccount: 0,
      destinationAccount: 0,
      amount: 0,
      date: new Date().toISOString().split('T')[0],
      note: '',
      returnType: 'Cash',
    })
    setSelectedRefund(null)
    setVendorBills([])
    setInsertData(null)
  }

  // Return type options (same as payments)
  const returnTypeOptions = [
    { value: 'Cash', label: 'Cash' },
    { value: 'Bank', label: 'Bank' },
  ]

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('refunds.title')}
        description={t('refunds.manageRefunds')}
        action={{
          label: t('refunds.addNew'),
          onClick: handleAddNew,
          icon: Plus,
        }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('refunds.refundDateFrom')}</label>
              <Input
                type="date"
                value={refundDateFrom}
                onChange={(e) => setRefundDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('refunds.refundDateTo')}</label>
              <Input
                type="date"
                value={refundDateTo}
                onChange={(e) => setRefundDateTo(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('refunds.dueDateFrom')}</label>
              <Input
                type="date"
                value={dueDateFrom}
                onChange={(e) => setDueDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('refunds.dueDateTo')}</label>
              <Input
                type="date"
                value={dueDateTo}
                onChange={(e) => setDueDateTo(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">{t('refunds.vendor')}</label>
              <Select
                value={selectedVendorId ? String(selectedVendorId) : 'all'}
                onValueChange={(value) => setSelectedVendorId(value === 'all' ? undefined : Number(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('refunds.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('refunds.all')}</SelectItem>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.vendorId || vendor.id} value={String(vendor.vendorId || vendor.id)}>
                      {vendor.vendorName || vendor.vendorNameEn || vendor.vendorNameAr || `Vendor ${vendor.vendorId || vendor.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-4 mt-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('refunds.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="h-4 w-4 mr-2" />
                {t('refunds.copy')}
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="h-4 w-4 mr-2" />
                {t('refunds.csv')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-muted-foreground">{t('refunds.loadingRefunds')}</div>
            </div>
          ) : filteredRefunds.length === 0 ? (
            <EmptyState
              title={t('refunds.noRefundsFound')}
              description={t('refunds.getStarted')}
              action={{
                label: t('refunds.addNewRefundAction'),
                onClick: handleAddNew,
              }}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.crNoteNo')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.billNo')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.vendor')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.date')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.dueDate')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('refunds.amount')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('common.edit')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        Payment Refund
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredRefunds.map((refund) => {
                      const refundId = refund.refundId
                      const refundNo = refund.refundNo || refund.refundNumber || refund.refundId || '-'
                      const billNo = refund.billNo || refund.billNumber || refund.billId || '-'
                      const vendorName = refund.vendorName || refund.vendorNameEn || refund.vendorNameAr || '-'
                      return (
                        <TableRow key={refundId} className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors">
                          <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                            {refundNo}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {billNo}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {vendorName}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatDate(refund.refundDate)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatDate(refund.dueDate)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {refund.amount || refund.total || 0}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(refund)}
                                className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleVendorCreditClick(refund)}
                                title="Vendor Credit"
                                className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <ArrowLeftRight className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {(totalPages > 1 || totalCount > 0) && (
                <div className="flex items-center justify-between px-6 py-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    {t('common.showing')} {((pageNumber - 1) * pageSize) + 1} {t('common.to')} {Math.min(pageNumber * pageSize, totalCount)} {t('common.of')} {totalCount} {t('common.entries')}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">{t('common.pageSize')}:</span>
                      <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                        <SelectTrigger className="w-[100px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                      disabled={pageNumber === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {t('common.previous')}
                    </Button>
                    <div className="text-sm">
                      {t('common.page')} {pageNumber} {t('common.of')} {totalPages || 1}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(p => Math.min(totalPages, p + 1))}
                      disabled={pageNumber >= totalPages}
                    >
                      {t('common.next')}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Vendor Credit Modal */}
      {showVendorCreditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Payment refund</h3>
              <button
                onClick={() => {
                  setShowVendorCreditModal(false)
                  resetVendorCreditForm()
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                disabled={vendorCreditSaving}
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {vendorCreditLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
                </div>
              ) : (
                <>
                  {/* Bill Dropdown (Disabled) */}
                  <div className="space-y-2">
                    <Label htmlFor="bill" className="text-gray-700 dark:text-gray-300 font-medium">
                      Bill
                    </Label>
                    <Select
                      value={String(vendorCreditForm.billId)}
                      disabled
                    >
                      <SelectTrigger id="bill" className="bg-gray-50 dark:bg-gray-800">
                        <SelectValue placeholder="Select bill" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendorBills.map((bill) => (
                          <SelectItem key={bill.billId} value={String(bill.billId)}>
                            {bill.billNo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Destination Account Dropdown */}
                  <div className="space-y-2">
                    <Label htmlFor="destinationAccount" className="text-gray-700 dark:text-gray-300 font-medium">
                      Destination Account *
                    </Label>
                    <Select
                      value={String(vendorCreditForm.destinationAccount)}
                      disabled
                    >
                      <SelectTrigger id="destinationAccount" className="bg-gray-50 dark:bg-gray-800">
                        <SelectValue placeholder="Select destination account" />
                      </SelectTrigger>
                      <SelectContent>
                        {accounts.map((account) => {
                          const accountId = account.accountId || account.id || 0
                          const accountName = account.accountNameEn || account.accountName || account.accountNameAr || ''
                          const accountCode = account.accountCode || ''
                          const displayLabel = accountCode ? `${accountCode} - ${accountName}` : accountName
                          return (
                            <SelectItem key={accountId} value={String(accountId)}>
                              {displayLabel}
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Amount */}
                  <div className="space-y-2">
                    <Label htmlFor="amount" className="text-gray-700 dark:text-gray-300 font-medium">
                      Amount *
                    </Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={vendorCreditForm.amount}
                      onChange={(e) =>
                        setVendorCreditForm((prev) => ({
                          ...prev,
                          amount: Number(e.target.value),
                        }))
                      }
                      disabled={vendorCreditSaving}
                      max={insertData?.maxReturnAmount}
                    />
                    {insertData && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Max return amount: {formatAmount(insertData.maxReturnAmount)}
                      </p>
                    )}
                  </div>

                  {/* Date */}
                  <div className="space-y-2">
                    <Label htmlFor="date" className="text-gray-700 dark:text-gray-300 font-medium">
                      Date *
                    </Label>
                    <Input
                      id="date"
                      type="date"
                      value={vendorCreditForm.date}
                      onChange={(e) =>
                        setVendorCreditForm((prev) => ({
                          ...prev,
                          date: e.target.value,
                        }))
                      }
                      disabled={vendorCreditSaving}
                    />
                  </div>

                  {/* Note */}
                  <div className="space-y-2">
                    <Label htmlFor="note" className="text-gray-700 dark:text-gray-300 font-medium">
                      Note
                    </Label>
                    <Input
                      id="note"
                      type="text"
                      value={vendorCreditForm.note}
                      onChange={(e) =>
                        setVendorCreditForm((prev) => ({
                          ...prev,
                          note: e.target.value,
                        }))
                      }
                      disabled={vendorCreditSaving}
                      placeholder="Enter note"
                    />
                  </div>

                  {/* Return Type */}
                  <div className="space-y-2">
                    <Label htmlFor="returnType" className="text-gray-700 dark:text-gray-300 font-medium">
                      Return Type *
                    </Label>
                    <Select
                      value={vendorCreditForm.returnType}
                      onValueChange={(value) =>
                        setVendorCreditForm((prev) => ({
                          ...prev,
                          returnType: value,
                        }))
                      }
                      disabled={vendorCreditSaving}
                    >
                      <SelectTrigger id="returnType">
                        <SelectValue placeholder="Select return type" />
                      </SelectTrigger>
                      <SelectContent>
                        {returnTypeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Modal Actions */}
                  <div className="flex space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowVendorCreditModal(false)
                        resetVendorCreditForm()
                      }}
                      className="flex-1"
                      disabled={vendorCreditSaving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={handleVendorCreditSave}
                      className="flex-1 bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white"
                      disabled={vendorCreditSaving}
                    >
                      {vendorCreditSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save'
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

